import { OverlayTrigger } from "react-bootstrap";
import ReactHtmlParser from 'react-html-parser';
import styled from 'styled-components';
import { unique } from "react-table/src/aggregations";
import { StyledExpensesTooltip } from "../../managers/reports/expensesMonthlyNew2/MonthlyExpensesNew.styled";
import FullPaid from "../../images/full_paid.svg";
import HalfPaid from "../../images/half_checked.svg";
import ScheduledToPay from "../../images/managers/scheduled_to_pay.png";
import FailedPayment from "../../images/managers/redCircle.png";
import EmptyCircle from "../../images/empty_circle.svg";
import TenantRecord from '../../images/tenant_claim_paid.svg';
import { i18n } from '../../common/translations';
import { constants } from "../constants/constants";
import BllinkUtils from "./utils";
import dayjs from "dayjs";

const StyledYearlyTooltip = styled(StyledExpensesTooltip)`
    .tooltip-inner {
        display: flex;
        flex-direction: column;
        color: #373647;
    }
`

const StringUtils = BllinkUtils.Strings;
const Money = BllinkUtils.Money;

export const parseValues = value => {
    const rounded = Money.roundNumber(value, 2);
    const withComma = StringUtils.numberWithCommas(rounded);
    return withComma;
};

export const renderIcon = (monthData, options, isAdmin, key, pdf) => {
    if (!monthData) return <div key={key}> </div>;

    const result = monthRelatedIconData(monthData);
    result.toolTipText = addPaymentMethodsTextIfNeeded(result.toolTipText, monthData);

    result.toolTipText = addChargeDateIfNeeded(result.toolTipText, monthData);
    result.toolTipText = addDepositDateIfNeeded(result.toolTipText, monthData);

    if (pdf) {
        return {
            percentage: options?.percentage?.paid?.value || '',
            paid: monthData?.paid?.value || '',
            planned: result.label,
        }
    }
    if (options?.percentage) result.toolTipText += options.percentage.paid.value;


    if (result.src) {
        return (
            <OverlayTrigger
                key={key}
                placement="bottom"
                overlay={props => (
                    <StyledYearlyTooltip {...props}>
                        {ReactHtmlParser(result.toolTipText)}
                    </StyledYearlyTooltip>
                )}
            >
                <div exceltext={result.excelText}>
                    <img className="paid-circle center" alt={result.alt} src={result.src}/>
                    <span className="month-label"> {parseValues(result.label)} </span>
                </div>
            </OverlayTrigger>
        );
    }

    return '';
};

const monthRelatedIconData = (monthData) => {
    const { total, paid, scheduled_to_pay, amount_left, isFailed, isFutureCharge } = monthData;
    let alt, src, label = total?.value, toolTipText, excelText = '';
    let fullOrOverPaid = total?.value <= paid?.value && paid?.value !== 0;
    const partlyPaid = paid?.value < total?.value && paid?.value > 0;
    let scheduledToPay = scheduled_to_pay?.value > 0;
    const failedPayment = isFailed;
    const futureCharge = isFutureCharge;
    fullOrOverPaid = fullOrOverPaid && !futureCharge;
    scheduledToPay = scheduledToPay || futureCharge;
    if (futureCharge) scheduled_to_pay.value = paid?.value;
    const renderEmpty = ((amount_left?.value > 0 || total?.value > 0) && paid?.value === 0 && scheduled_to_pay?.value === 0) || total?.value === 0;
    monthData.payment_methods = unique(monthData?.payment_methods);
    const isTenantRecord = monthData.payment_methods?.includes(constants.managerPaymentsOptions.tenantRecordKey);
    const isLegacy = monthData.payment_methods.includes(constants.managerPaymentsOptions.legacyPaymentKey);

    if (isTenantRecord) {
        alt = 'Tenant Claim';
        label = paid.value;
        excelText = paid.value;
        // updated icon due to https://bllink.atlassian.net/browse/SA1-155
        if (monthData.is_tenant_record_not_approved) {
            src = TenantRecord;
            toolTipText = `<div>${i18n.t('reports.tenant_report_marked_as_paid')}</div>`;
        } else {
            src = FullPaid;
            toolTipText = `<div>${i18n.t('reports.month_marked_as_paid')}</div>`;
        }
        //
    // } else if (isLegacy) {
    //     alt = 'Legacy Payments';
    //     src = FullPaid;
    //     toolTipText = `<div>${i18n.t('reports.month_marked_as_paid')}</div>`;
    } else if (failedPayment) {
            alt = 'Failed To Pay';
            src = FailedPayment;
            toolTipText = `<div>${i18n.t('reports.failed_payment')}</div>`;
            excelText = total.value;
        } else if (fullOrOverPaid) {
            alt = 'Full Paid';
            src = FullPaid;
            label = paid.value;
            toolTipText = `<div>${i18n.t('reports.paid_full_new')}</div>`;
            excelText = paid.value;
        } else if (partlyPaid) {
            alt = 'Half Paid';
            src = HalfPaid;
            toolTipText = `<div>${i18n.t('reports.paid_out_of_total', {paid: parseValues(paid.value), total: parseValues(total.value)})}</div>`;
            excelText = `${paid.value} / ${total.value}`;
        } else if (scheduledToPay) {
            alt = 'Scheduled To Pay';
            src = ScheduledToPay;
            toolTipText = `<div>${i18n.t('reports.scheduled_to_pay', {scheduledToPay: monthData.scheduled_to_pay.value, total: monthData.total.value})}</div>`;
            excelText = scheduled_to_pay.value
        } else if (renderEmpty) {
            alt = 'Empty';
            src = EmptyCircle;
            toolTipText = `<div>${i18n.t('reports.none_paid')}</div>`;
            excelText = amount_left?.value || total?.value;
        }


    return {
        alt: alt,
        src: src,
        label: label,
        toolTipText: toolTipText,
        excelText: excelText
    };
};

const addPaymentMethodsTextIfNeeded = (tooltipText, monthData) => {
    const { payment_methods } = monthData;
    if (payment_methods) {
        tooltipText = tooltipText || '';
        let isFirst = true;
        let methodsAsString;
        if (payment_methods && payment_methods.length > 0) {
            for (const paymentMethod of payment_methods) {
                if (constants.managerPaymentsOptions.isValidMethod(paymentMethod)) {
                    if (isFirst) {
                        methodsAsString = i18n.t(`buildings.${paymentMethod}`);
                        isFirst = false
                    } else methodsAsString += ` + ${i18n.t(`buildings.${paymentMethod}`)}`;
                }
            }
            tooltipText += `<div><span>${i18n.t('reports.paymentMethod')}</span> <b>${methodsAsString}</b></div>`;
        }
    }

    return tooltipText;
};

const addChargeDateIfNeeded = (tooltipText, monthData) => {
    const {charge_date, payment_methods} = monthData;

    if (payment_methods.includes('legacyPayment') || payment_methods.includes('tenantRecord')) {
        return tooltipText;
    }

    if (!charge_date) {
        return tooltipText;
    }

    tooltipText = tooltipText || '';
    tooltipText += `<div>${i18n.t('reports.charge_date')} <span>${dayjs(charge_date).format('DD/MM/YYYY')}</span></div>`;

    return tooltipText;
};

const addDepositDateIfNeeded = (tooltipText, monthData) => {
    const { deposit_date, payment_methods } = monthData;

    if (payment_methods.includes('legacyPayment') || payment_methods.includes('tenantRecord')) {
        return tooltipText;
    }

    if (!deposit_date) {
        return tooltipText;
    }

    tooltipText = tooltipText || '';
    tooltipText += `<div>${i18n.t('reports.deposit_date')} <span>${dayjs(deposit_date).format('DD/MM/YYYY')}</span></div>`;

    return tooltipText;
};

export const renderOneTimeIcon = (oneTimeData, isAdmin) => {
    if (!oneTimeData) return <div/>;

    const result = oneTimeIconData(oneTimeData, isAdmin);
    result.toolTipText = addPaymentMethodsTextIfNeeded(result.toolTipText, oneTimeData);

    if (result.src) {
        return (
            <OverlayTrigger
                placement="bottom"
                overlay={props => (
                    <StyledYearlyTooltip {...props}>
                        {ReactHtmlParser(result.toolTipText)}
                    </StyledYearlyTooltip>
                )}
            >
                <div exceltext={result.excelText}>
                    <img className="paid-circle center" alt={result.alt} src={result.src}/>
                    <span className="month-label"> {parseValues(result.label)} </span>
                </div>
            </OverlayTrigger>
        );
    }

    if (result.renderEmpty) {
        return <div></div>
    }
}

const oneTimeIconData = (oneTimeData, isAdmin) => {
    const { total, paid, scheduled_to_pay } = oneTimeData;
    let alt, src, label = total.value, toolTipText = '', excelText = '', renderEmpty = true;
    const fullOrOverPaid = total?.value <= paid.value;
    const didntPay = paid.value === 0 && scheduled_to_pay.value === 0;
    const partlyPaid = paid.value < total?.value && paid.value > 0;
    const scheduledToPay = scheduled_to_pay.value > 0;

    oneTimeData = {
        ...oneTimeData,
        payment_methods: unique(oneTimeData.payment_methods),
    };
    const isTenantRecord = oneTimeData.payment_methods?.includes(constants.managerPaymentsOptions.tenantRecordKey);

    if (isTenantRecord) {
        alt = 'Tenant Claim';
        label = paid.value;
        excelText = paid.value;
        // updated icon due to https://bllink.atlassian.net/browse/SA1-155
        if (oneTimeData.is_tenant_record_not_approved) {
            src = TenantRecord;
            toolTipText = `<div>${i18n.t('reports.tenant_report_marked_as_paid')}</div>`;
        } else {
            src = FullPaid;
            toolTipText = `<div>${i18n.t('reports.month_marked_as_paid')}</div>`;
        }
    }else if (fullOrOverPaid) {
            alt = 'Full Paid';
            src = FullPaid;
            label = paid.value;
            toolTipText = i18n.t('reports.paid_full_new');
            excelText = paid.value;
        } else if (didntPay) {
            renderEmpty = true;
        } else if (partlyPaid) {
            alt = 'Half Paid';
            src = HalfPaid;
            label = total?.value;
            toolTipText = i18n.t('reports.paid_out_of_total', {paid: parseValues(paid.value), total: parseValues(total.value)});
            excelText = paid.value;
        } else if (scheduledToPay) {
            alt = 'Scheduled To Pay';
            src = ScheduledToPay;
            toolTipText += i18n.t('reports.scheduled_to_pay_only_new');
        }


    return {
        alt: alt,
        src: src,
        label: label,
        toolTipText: toolTipText,
        excelText: excelText,
        renderEmpty: renderEmpty,
    };
}

export const renderTotalIcon = total => {
    if (!total) return <div/>;

    const { charged, debt, future } = total;

    const result = totalIconData(total);
    if (charged) result.toolTipText += `<div>${i18n.t('reports.total_charged')}: ${parseValues(charged.value)}`;
    if (debt) result.toolTipText += `<div>${i18n.t('reports.total_debt_col')}: ${parseValues(debt.value)}`;
    if (future) result.toolTipText += `<div>${i18n.t('reports.total_future')}: ${parseValues(future.value)}`;

    if (result.src) {
        return (
            <OverlayTrigger
                placement="bottom"
                overlay={props => (
                    <StyledYearlyTooltip {...props}>
                        {ReactHtmlParser(result.toolTipText)}
                    </StyledYearlyTooltip>
                )}
            >
                <div exceltext={result.excelText}>
                    <img className="paid-circle center" alt={result.alt} src={result.src}/>
                    <span className="month-label"> {parseValues(result.label)} </span>
                </div>
            </OverlayTrigger>
        );
    }
};

const totalIconData = totalData => {
    const { charged, debt, future } = totalData;
    const totalToPay = debt?.value + future?.value + charged?.value;
    let alt, src, label = totalToPay, toolTipText, excelText = '';
    let fullOrOverPaid = totalToPay <= charged?.value;
    const partlyPaid = charged?.value < totalToPay && charged?.value > 0;
    let scheduledToPay = future?.value > 0;
    const renderEmpty = charged?.value === 0;

    totalData.payment_methods = unique(totalData?.payment_methods);
    const isTenantRecord = totalData.payment_methods?.includes(constants.managerPaymentsOptions.tenantRecordKey)
        && totalData?.is_tenant_record_not_approved;

    if (isTenantRecord) {
        alt = 'Tenant Claim';
        src = TenantRecord;
        label = charged.value;
        toolTipText = i18n.t('reports.tenant_claim_paid_explanation', {paid: parseValues(charged.value)});
        excelText = charged.value;
    } else if (fullOrOverPaid) {
        alt = 'Full Paid';
        src = FullPaid;
        label = charged.value;
        toolTipText = i18n.t('reports.paid_full_new');
        excelText = charged.value;
    } else if (partlyPaid) {
        alt = 'Half Paid';
        src = HalfPaid;
        toolTipText = i18n.t('reports.paid_out_of_total', {paid: parseValues(charged.value), total: parseValues(totalToPay)});
        excelText = `${charged.value} / ${totalToPay}`;
    } else if (scheduledToPay) {
        alt = 'Scheduled To Pay';
        src = ScheduledToPay;
        toolTipText = i18n.t('reports.scheduled_to_pay_only_new');
        excelText = future.value
    } else if (renderEmpty) {
        alt = 'Empty';
        src = EmptyCircle;
        toolTipText = i18n.t('reports.none_paid');
        excelText = totalToPay;
    }

    return {
        alt: alt,
        src: src,
        label: label,
        toolTipText: toolTipText,
        excelText: excelText
    };
};
